import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useOnboardingState } from '../../hooks/useOnboardingState.jsx';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import Logo from '@/Asset/images/logo-blaxk.webp';
import VisualImage from '@/Asset/images/step-3.webp';
import EastIcon from '@mui/icons-material/East';
import Toast from '@/utils/toastManager';
import { isValidUrlOrInstagram } from '@/utils/validation';
import normalizeWebsiteLink from '@/TRC/pages/Access/utils/normalizeWebsiteLink';
import StepIndicator from '../StepIndicator/StepIndicator';
import './Step4.css';

const Step4 = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { state, updateState } = useOnboardingState();
	const roleFromQuery = useMemo(() => new URLSearchParams(location.search).get('role'), [location.search]);
	const planFromQuery = useMemo(() => new URLSearchParams(location.search).get('plan'), [location.search]);
	const isRetake = useMemo(
		() => new URLSearchParams(location.search).get('retake') === 'true' || location.state?.isRetake === true,
		[location.search, location.state?.isRetake]
	);
	const selectedRole = roleFromQuery || state.onboardingRole || null;
	const isStudioPlan = state.selectedPlanType === 'studio' || planFromQuery === 'studio';
	const isDancerParentFlow = !isStudioPlan && selectedRole === 'dancer-parent';
	const isStandardAltFlow = !isStudioPlan && !isDancerParentFlow;
	const totalSteps = isStudioPlan ? (isRetake ? 5 : 6) : 4;
	const stepItems = Array.from({ length: totalSteps }, (_, index) => index + 1);
	const [wishlist, setWishlist] = useState('');
	const [websiteInput, setWebsiteInput] = useState('');
	const [trcGoalsInput, setTrcGoalsInput] = useState('');
	const [selectedStudioUsage, setSelectedStudioUsage] = useState([]);

	// Validation function for StepIndicator - checks if current step is valid
	const validateStep = () => {
		if (isStudioPlan && selectedStudioUsage.length === 0) {
			Toast.error('Please select at least one usage option to continue.');
			return false;
		}
		return true;
	};

	// Handle validation failure for StepIndicator
	const handleValidationFail = () => {
		// Toast is already shown in validateStep
	};
	const studioUsageOptions = useMemo(
		() => [
			{ id: 'weekly-general-technique', label: 'Weekly / General Technique Classes' },
			{ id: 'strength-conditioning', label: 'Strength / Conditioning Classes' },
			{ id: 'flexibility-classes', label: 'Flexibility Classes' },
			{ id: 'teacher-training-resource', label: 'Teacher Training Resource' },
			{ id: 'substitute-lesson-planning', label: 'Substitute Lesson Planning' }
		],
		[]
	);

	useEffect(() => {
		if (roleFromQuery && roleFromQuery !== state.onboardingRole) {
			updateState({ onboardingRole: roleFromQuery });
		}
	}, [roleFromQuery, state.onboardingRole, updateState]);

	const goNext = async (extraState = {}) => {
		// Validate required selections for studio flow
		if (isStudioPlan && selectedStudioUsage.length === 0) {
			Toast.error('Please select at least one usage option to continue.');
			return;
		}

		const normalizedWebsiteInput = websiteInput.trim() ? normalizeWebsiteLink(websiteInput.trim()) : '';
		const onboardingPreferences = {
			...(state.onboardingPreferences || {}),
			parentWishlist: wishlist,
			trcInstagramWebsite: normalizedWebsiteInput,
			trcGoals: trcGoalsInput,
			studioUsageSelections: selectedStudioUsage,
			...('trcRequestStatus' in extraState ? { trcRequestStatus: extraState.trcRequestStatus } : {})
		};

		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 8,
				onboardingPreferences
			});
		} catch (error) {
			Toast.error('Failed to save this step. Please try again.');
			return;
		}

		updateState({
			currentStep: 8,
			parentWishlist: wishlist,
			trcInstagramWebsite: normalizedWebsiteInput,
			trcGoals: trcGoalsInput,
			studioUsageSelections: selectedStudioUsage,
			onboardingPreferences,
			...extraState
		});

		if (isStudioPlan || state.userType === 'studio-owner') {
			navigate(`/onboarding/step-5?${new URLSearchParams(location.search).toString()}`);
			return;
		}

		navigate(`/onboarding/recommended-videos?${new URLSearchParams(location.search).toString()}`);
	};

	const handleRequestAccess = async () => {
		if (!websiteInput.trim()) {
			Toast.error('Please provide your Instagram or Website.');
			return;
		}
		if (!isValidUrlOrInstagram(websiteInput)) {
			Toast.error('Enter a valid URL or Instagram handle');
			return;
		}
		await goNext({ trcRequestStatus: 'requested' });
	};

	const handleSkipAndSeeResults = async () => {
		await goNext({ trcRequestStatus: 'skipped' });
	};

	const toggleStudioUsage = id => {
		setSelectedStudioUsage(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
	};

	const handleSkip = async () => {
		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 8
			});
		} catch (error) {
			Toast.error('Failed to save this step. Please try again.');
			return;
		}

		updateState({ currentStep: 8 });
		navigate('/');
	};

	return (
		<div className="onboarding-step-four">
			<div className="onboarding-step-four__grid-overlay" />
			<div className="onboarding-step-four__content">
				<header className="onboarding-step-four__header">
					<div className="onboarding-step-four__brand-row">
						<button
							type="button"
							className="onboarding-step-four__back-btn"
							onClick={() => {
								const params = new URLSearchParams(location.search).toString();
								const path = isRetake && isStandardAltFlow ? '/onboarding/step-2' : '/onboarding/step-3';
								navigate(`${path}?${params}`);
							}}
						>
							<KeyboardBackspaceOutlinedIcon />
						</button>
						<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-step-four__logo" />
					</div>

					<StepIndicator
						totalSteps={totalSteps}
						currentStep={4}
						validateStep={validateStep}
						onValidationFail={handleValidationFail}
						basePath="/onboarding"
					/>
				</header>

				<main className="onboarding-step-four__main">
					{isDancerParentFlow ? (
						<section className="onboarding-step-four__layout">
							<div className="onboarding-step-four__main-panel">
								<h1 className="onboarding-step-four__title">What Would You Like To See On Godfrey Method On Demand?</h1>
								<div className="onboarding-step-four__input-wrap">
									<textarea
										className="onboarding-step-four__input"
										placeholder="Optional"
										value={wishlist}
										onChange={event => setWishlist(event.target.value)}
									/>
								</div>
								<button type="button" onClick={() => goNext()} className="onboarding-step-four__cta-btn">
									See My Results <EastIcon />
								</button>
							</div>
						</section>
					) : isStandardAltFlow ? (
						<section className="onboarding-step-four__layout">
							<div className="onboarding-step-four__visual-wrap" aria-hidden="true">
								<img src={VisualImage} alt="" className="onboarding-step-four__visual" />
							</div>
							<div className="onboarding-step-four__main-panel-trc">
								<h1 className="onboarding-step-four__title">Request Access To Teacher Resource Center</h1>
								<p className="onboarding-step-four__description">
									Access exclusive content and resources to enhance your teaching skills.
								</p>

								<div className="onboarding-step-four__label">Instagram Or Website</div>
								<div className="onboarding-step-four__input-wrap">
									<div className="onboarding-step-four__input-with-icon">
										<LanguageOutlinedIcon />
										<input
											type="text"
											className="onboarding-step-four__input onboarding-step-four__input--single"
											placeholder="instagram.com/thegodfreymethod"
											value={websiteInput}
											onChange={event => setWebsiteInput(event.target.value)}
										/>
									</div>
								</div>

								<div className="onboarding-step-four__label">
									What are you hoping to gain from teacher resource center?
								</div>
								<div className="onboarding-step-four__input-wrap">
									<textarea
										className="onboarding-step-four__input"
										placeholder="Optional"
										value={trcGoalsInput}
										onChange={event => setTrcGoalsInput(event.target.value)}
									/>
								</div>

								<div className="onboarding-step-four__actions-row">
									<button
										type="button"
										onClick={handleRequestAccess}
										className={`onboarding-step-four__outline-btn ${!websiteInput.trim() ? 'is-disabled' : ''}`}
									>
										Request Access
									</button>
									<button type="button" onClick={handleSkipAndSeeResults} className="onboarding-step-four__cta-btn">
										Skip & See Results <EastIcon />
									</button>
								</div>
							</div>
						</section>
					) : isStudioPlan ? (
						<section className="onboarding-step-four__studio">
							<h1 className="onboarding-step-four__title">How Do You Plan To Use Your Studio Account?</h1>
							<p className="onboarding-step-four__subtitle">Please select all that apply</p>
							<div className="onboarding-step-four__cards onboarding-step-four__cards--five">
								{studioUsageOptions.map(option => {
									const isSelected = selectedStudioUsage.includes(option.id);
									return (
										<button
											key={option.id}
											type="button"
											className={`onboarding-step-four__card is-studio ${isSelected ? 'is-selected' : ''}`}
											onClick={() => toggleStudioUsage(option.id)}
										>
											<p className="onboarding-step-four__studio-card-label">{option.label}</p>
											<span className={`onboarding-step-four__studio-card-dot ${isSelected ? 'is-selected' : ''}`}>
												{isSelected ? <CheckIcon /> : null}
											</span>
										</button>
									);
								})}
							</div>
						</section>
					) : (
						<div className="onboarding-step-four__placeholder">
							<h1 className="onboarding-step-four__title">Step 4</h1>
						</div>
					)}
				</main>

				<footer className="onboarding-step-four__footer">
					{!isDancerParentFlow && !isStandardAltFlow ? (
						<button
							type="button"
							onClick={() => goNext()}
							className={`onboarding-step-four__continue-btn ${isStudioPlan && selectedStudioUsage.length === 0 ? 'is-disabled' : ''}`}
							disabled={isStudioPlan && selectedStudioUsage.length === 0}
						>
							Continue <EastIcon />
						</button>
					) : null}
					{!isStandardAltFlow ? (
						<button type="button" onClick={handleSkip} className="onboarding-step-four__skip-btn">
							Skip
						</button>
					) : null}
				</footer>
			</div>
		</div>
	);
};

export default Step4;
